import { useState, useEffect, useMemo } from "react";
import { social, getPlatformInfo, type SocialAccount, type SocialPost, type PostAnalytics, type AccountInsights, type InsightMetric } from "../lib/social";
import { PlatformIcon } from "../components/PlatformIcon";
import { useTheme } from "../lib/theme";
import { useAccounts } from "../lib/app-data";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

function useChartColors() {
  const { resolved } = useTheme();
  const dark = resolved === "dark";
  return {
    grid: dark ? "#27272a" : "#f5f5f4",
    axis: dark ? "#71717a" : "#a8a29e",
    tooltipBg: dark ? "#18181b" : "#ffffff",
    tooltipBorder: dark ? "#3f3f46" : "#e5e5e5",
    tooltipText: dark ? "#e4e4e7" : "#1c1917",
  };
}

function VideoStat({ label, value, delta }: { label: string; value: number; delta?: number }) {
  return (
    <div className="text-center min-w-[52px]">
      <p className="font-bold text-stone-800 dark:text-zinc-200">{value.toLocaleString()}</p>
      {delta != null && delta !== 0 && (
        <p className={`text-[9px] font-semibold ${delta > 0 ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}>
          {delta > 0 ? "+" : ""}{delta.toLocaleString()}
        </p>
      )}
      <p className="text-[9px] uppercase tracking-wide">{label}</p>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 p-5 text-center">
      <p className="text-2xl font-bold text-stone-900 dark:text-white">{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="text-xs uppercase text-stone-400 dark:text-zinc-600 font-semibold tracking-wide mt-1">
        {label.replace(/_/g, " ").replace(/^page /, "")}
      </p>
    </div>
  );
}

function formatMoney(amount: number, currency: string | null): string {
  // Meta returns earnings as decimal numbers (already in major currency units)
  // — we don't want to divide by 100. Currency may be missing for unenrolled-but-recent pages.
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || ""}`.trim();
  }
}

const EARNINGS_LABELS: Record<string, string> = {
  approximate: "Approximate (Dashboard)",
  content_monetization_program: "Content Monetization (Finalized)",
  videos: "Videos",
  reels: "Reels",
  photos: "Photos",
  text: "Text",
  stories: "Stories",
  photos_text_stories: "Photos / Text / Stories",
  extra_bonus: "Extra Bonus",
  in_stream: "In-Stream",
  other: "Other",
};

function EarningsCard({
  data,
}: {
  data: {
    enrolled: boolean;
    total: number | null;
    currency: string | null;
    breakdown: Record<string, number> | null;
    period?: { since: number; until: number };
  };
}) {
  const total = data.total ?? 0;
  const currency = data.currency;
  const breakdown = Object.entries(data.breakdown || {})
    .filter(([_, v]) => typeof v === "number")
    .sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-3 tracking-widest">
        Content Monetization Earnings
      </p>
      <div className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-500/10 dark:to-zinc-900 rounded-2xl border border-emerald-200/60 dark:border-emerald-500/20 p-6">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <div>
            <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">
              {formatMoney(total, currency)}
            </p>
            <p className="text-xs text-stone-500 dark:text-zinc-500 mt-0.5">
              Approximate earnings · paid by Meta
            </p>
          </div>
          {breakdown.length > 0 && (
            <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 tracking-widest">
              Breakdown
            </p>
          )}
        </div>
        {breakdown.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {breakdown.map(([key, value]) => (
              <div
                key={key}
                className="bg-white/70 dark:bg-zinc-900/60 rounded-xl border border-stone-200/60 dark:border-zinc-800/60 p-3"
              >
                <p className="text-base font-bold text-stone-900 dark:text-white">
                  {formatMoney(value, currency)}
                </p>
                <p className="text-[10px] uppercase text-stone-400 dark:text-zinc-600 font-semibold tracking-wide mt-1">
                  {EARNINGS_LABELS[key] || key.replace(/_/g, " ")}
                </p>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-stone-400 dark:text-zinc-600 mt-4 leading-relaxed">
          Meta updates these figures with a 1–3 day lag and the totals are approximate (final payouts may differ
          slightly). Stars, Subscriptions and Live Ads are tracked separately.
        </p>
      </div>
    </div>
  );
}

function formatMetricName(name: string): string {
  return name
    .replace(/^page_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const CHART_COLORS = [
  "#f97316", "#3b82f6", "#10b981", "#8b5cf6", "#ef4444", "#06b6d4", "#f59e0b", "#ec4899"
];

const DATE_RANGES = [
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "28 days", days: 28 },
  { label: "90 days", days: 90 },
];

export function Analytics() {
  const [tab, setTab] = useState<"insights" | "posts">("insights");
  const { accounts: allAccounts, loading: accountsLoading } = useAccounts();
  const accounts = useMemo<SocialAccount[]>(
    () => allAccounts.filter((a) => a.status === "active"),
    [allAccounts],
  );
  const loading = accountsLoading;
  const [error, setError] = useState("");

  // Insights state
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState(28);
  const [insights, setInsights] = useState<AccountInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState("");

  // Post analytics state
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [postAnalytics, setPostAnalytics] = useState<PostAnalytics | null>(null);
  const [postAnalyticsLoading, setPostAnalyticsLoading] = useState(false);
  const [postAnalyticsError, setPostAnalyticsError] = useState("");

  // Pick a default account once the shared store finishes loading
  useEffect(() => {
    if (selectedAccountId == null && accounts.length > 0) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  useEffect(() => {
    if (selectedAccountId && tab === "insights") {
      loadInsights(selectedAccountId, dateRange);
    }
  }, [selectedAccountId, tab, dateRange]);

  async function loadInsights(accountId: number, days: number) {
    setInsightsLoading(true);
    setInsightsError("");
    setInsights(null);
    try {
      // Use start of today as "until" so we don't get partial/future data
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const until = Math.floor(today.getTime() / 1000);
      const since = until - days * 86400;
      const res = await social.getAccountInsights(accountId, { since, until });
      setInsights(res.data);
    } catch (err: any) {
      setInsightsError(err.message);
    } finally {
      setInsightsLoading(false);
    }
  }

  async function loadPosts() {
    setPostsLoading(true);
    try {
      const res = await social.listPosts({ sort: "created_at:desc", limit: 50 });
      setPosts((res.data || []).filter(p => p.status === "published" || p.status === "partial"));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPostsLoading(false);
    }
  }

  useEffect(() => {
    if (tab === "posts" && posts.length === 0) {
      loadPosts();
    }
  }, [tab]);

  async function openPostAnalytics(post: SocialPost) {
    setSelectedPost(post);
    setPostAnalytics(null);
    setPostAnalyticsError("");
    setPostAnalyticsLoading(true);
    try {
      const res = await social.getPostAnalytics(post.id);
      setPostAnalytics(res.data);
    } catch (err: any) {
      setPostAnalyticsError(err.message);
    } finally {
      setPostAnalyticsLoading(false);
    }
  }

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-stone-900 dark:text-white tracking-tight">Analytics</h2>
          <p className="text-sm text-stone-500 dark:text-zinc-500 mt-1">
            Page insights and post performance
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl mb-4 text-sm border border-red-200/60 dark:border-red-500/20 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError("")} className="text-red-400 hover:text-red-600 dark:hover:text-red-300 text-xs font-medium ml-3">dismiss</button>
        </div>
      )}

      {/* Tabs + Date Range */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex gap-1 bg-stone-100 dark:bg-zinc-800/50 rounded-xl p-1 w-fit">
          <button
            onClick={() => setTab("insights")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === "insights"
                ? "bg-white dark:bg-zinc-900 text-stone-900 dark:text-white shadow-sm"
                : "text-stone-500 dark:text-zinc-500 hover:text-stone-700 dark:hover:text-zinc-300"
            }`}
          >
            Page Insights
          </button>
          <button
            onClick={() => setTab("posts")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === "posts"
                ? "bg-white dark:bg-zinc-900 text-stone-900 dark:text-white shadow-sm"
                : "text-stone-500 dark:text-zinc-500 hover:text-stone-700 dark:hover:text-zinc-300"
            }`}
          >
            Post Analytics
          </button>
        </div>

        {tab === "insights" && (
          <div className="flex gap-1 bg-stone-100 dark:bg-zinc-800/50 rounded-xl p-1">
            {DATE_RANGES.map(r => (
              <button
                key={r.days}
                onClick={() => setDateRange(r.days)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  dateRange === r.days
                    ? "bg-white dark:bg-zinc-900 text-stone-900 dark:text-white shadow-sm"
                    : "text-stone-500 dark:text-zinc-500 hover:text-stone-700 dark:hover:text-zinc-300"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tab === "insights" ? (
        <InsightsView
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          selectedAccount={selectedAccount || null}
          onSelectAccount={setSelectedAccountId}
          insights={insights}
          insightsLoading={insightsLoading}
          insightsError={insightsError}
          onReload={() => selectedAccountId && loadInsights(selectedAccountId, dateRange)}
        />
      ) : (
        <PostAnalyticsView
          posts={posts}
          postsLoading={postsLoading}
          selectedPost={selectedPost}
          postAnalytics={postAnalytics}
          postAnalyticsLoading={postAnalyticsLoading}
          postAnalyticsError={postAnalyticsError}
          onSelectPost={openPostAnalytics}
          onClose={() => { setSelectedPost(null); setPostAnalytics(null); }}
        />
      )}
    </div>
  );
}

/* ─── Page Insights View ─── */

function InsightsView({
  accounts,
  selectedAccountId,
  selectedAccount,
  onSelectAccount,
  insights,
  insightsLoading,
  insightsError,
  onReload,
}: {
  accounts: SocialAccount[];
  selectedAccountId: number | null;
  selectedAccount: SocialAccount | null;
  onSelectAccount: (id: number) => void;
  insights: AccountInsights | null;
  insightsLoading: boolean;
  insightsError: string;
  onReload: () => void;
}) {
  if (accounts.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-stone-100 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-stone-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <p className="text-stone-500 dark:text-zinc-500 mb-2">No connected accounts</p>
        <p className="text-stone-400 dark:text-zinc-600 text-sm">Connect a social account to see insights</p>
      </div>
    );
  }

  return (
    <div>
      {/* Account selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {accounts.map(account => {
          const info = getPlatformInfo(account.platform);
          const isSelected = account.id === selectedAccountId;
          return (
            <button
              key={account.id}
              onClick={() => onSelectAccount(account.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                isSelected
                  ? "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30 text-orange-700 dark:text-orange-300"
                  : "bg-white dark:bg-zinc-900 border-stone-200/60 dark:border-zinc-800/60 text-stone-600 dark:text-zinc-400 hover:border-stone-300 dark:hover:border-zinc-700"
              }`}
            >
              <span className={`w-5 h-5 ${info.color} text-white rounded flex items-center justify-center flex-shrink-0`}>
                <PlatformIcon platform={account.platform} className="w-3 h-3" />
              </span>
              <span className="truncate max-w-[150px]">{account.platform_display_name || account.platform_username}</span>
            </button>
          );
        })}
      </div>

      {insightsLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : insightsError ? (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200/60 dark:border-red-500/20 rounded-xl p-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{insightsError}</p>
          <button onClick={onReload} className="text-red-500 hover:text-red-700 dark:hover:text-red-300 text-xs font-medium mt-2">Retry</button>
        </div>
      ) : insights ? (
        <div className="space-y-6">
          {/* Message for unsupported platforms */}
          {(insights as any).message && (
            <div className="bg-stone-50 dark:bg-zinc-800/50 border border-stone-200/60 dark:border-zinc-800/60 rounded-xl p-4">
              <p className="text-stone-500 dark:text-zinc-500 text-sm">{(insights as any).message}</p>
            </div>
          )}

          {/* Current stats */}
          {insights.current && Object.keys(insights.current).filter(k => insights.current[k] != null).length > 0 && (
            <div>
              <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-3 tracking-widest">Current Stats</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(insights.current)
                  .filter(([_, v]) => v != null && typeof v !== "boolean")
                  .map(([key, value]) => (
                    <MetricCard key={key} label={key} value={value!} />
                  ))}
              </div>
            </div>
          )}

          {/* Content Monetization earnings (Facebook only, when page is enrolled) */}
          {insights.monetization?.enrolled && insights.monetization.total != null && (
            <EarningsCard data={insights.monetization} />
          )}

          {/* Metrics */}
          {insights.metrics && Object.keys(insights.metrics).length > 0 && (() => {
            const entries = Object.entries(insights.metrics).filter(([k]) => !k.startsWith('_'));
            // Filter out time series that are all zeros (e.g. Instagram follower_count bug)
            const allTimeSeries = entries.filter(([_, m]) =>
              m.values && m.values.length > 1 && m.values.some((v: any) => v.value !== 0)
            );
            const totalOnly = entries.filter(([_, m]) => (!m.values || m.values.length <= 1) && (m as any).total_value?.value != null);

            // Split: platform API data (period=day) vs our tracked snapshots (period=lifetime / _history suffix)
            const platformSeries = allTimeSeries.filter(([k, m]) => !k.endsWith('_history') && m.period !== 'lifetime');
            const trackedSeries = allTimeSeries.filter(([k, m]) => k.endsWith('_history') || m.period === 'lifetime');

            return (
              <div>
                {/* Platform insights — native API time series */}
                {platformSeries.length > 0 && (
                  <>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-3 tracking-widest">Platform Insights</p>
                    {platformSeries.length > 1 && (
                      <OverviewChart metrics={Object.fromEntries(platformSeries)} />
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                      {platformSeries.map(([metricName, metric], idx) => (
                        <MetricChart key={metricName} name={metricName} metric={metric} color={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </div>
                  </>
                )}

                {/* Tracked metrics — our stored daily snapshots */}
                {trackedSeries.length > 0 && (
                  <>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-3 mt-6 tracking-widest">Tracked Metrics</p>
                    {trackedSeries.length > 1 && (
                      <OverviewChart metrics={Object.fromEntries(trackedSeries)} />
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                      {trackedSeries.map(([metricName, metric], idx) => (
                        <MetricChart key={metricName} name={metricName} metric={metric} color={CHART_COLORS[(idx + 4) % CHART_COLORS.length]} />
                      ))}
                    </div>
                  </>
                )}

                {/* Totals comparison chart for metrics without daily breakdown */}
                {totalOnly.length > 0 && (
                  <>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-3 mt-6 tracking-widest">Period Totals</p>
                    <TotalsChart metrics={totalOnly} />
                  </>
                )}
              </div>
            );
          })()}

          {/* Top content list — TikTok uses _top_videos (from API), others use _top_posts (from stored metrics) */}
          {insights.metrics && ((insights.metrics as any)._top_videos || (insights.metrics as any)._top_posts) && (
            <TopPostsList
              posts={(insights.metrics as any)._top_videos || (insights.metrics as any)._top_posts}
              platform={insights.platform}
            />
          )}

          {/* No metrics available */}
          {(!insights.metrics || Object.keys(insights.metrics).length === 0) && !(insights as any).message && (
            <div className="text-center py-8">
              <p className="text-stone-500 dark:text-zinc-500 text-sm">No metric data available for this account</p>
              <p className="text-stone-400 dark:text-zinc-600 text-xs mt-1">Some platforms may take time to generate insights</p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* ─── Top Posts/Videos List with sort toggle ─── */

function TopPostsList({ posts, platform }: { posts: any[]; platform: string }) {
  const [sort, setSort] = useState<"views" | "recent" | "growth">("recent");

  const primaryMetric = (platform === "pinterest") ? "impressions" : "views";
  const label = (platform === "tiktok" || platform === "youtube") ? "Videos" : "Posts";

  const sorted = [...posts].sort((a, b) => {
    if (sort === "recent") {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    }
    if (sort === "growth") {
      const ga = a.daily_change?.[primaryMetric] || a.daily_change?.likes || 0;
      const gb = b.daily_change?.[primaryMetric] || b.daily_change?.likes || 0;
      return gb - ga;
    }
    return (b[primaryMetric] || b.likes || 0) - (a[primaryMetric] || a.likes || 0);
  });

  const SORT_OPTIONS = [
    { key: "recent" as const, label: "Recent" },
    { key: "views" as const, label: primaryMetric === "impressions" ? "Top Impressions" : "Top Views" },
    { key: "growth" as const, label: "Growth" },
  ];

  // Fixed columns per platform — always show same stats for consistent layout
  const getStats = (post: any) => {
    const dc = post.daily_change || {};

    if (platform === "pinterest") {
      return [
        { label: "impressions", value: post.impressions ?? 0, delta: dc.impressions },
        { label: "saves", value: post.saves ?? 0, delta: dc.saves },
        { label: "clicks", value: post.clicks ?? 0, delta: dc.clicks },
      ];
    }
    if (platform === "instagram") {
      return [
        { label: "reach", value: post.reach ?? post.views ?? 0, delta: dc.reach ?? dc.views },
        { label: "likes", value: post.likes ?? 0, delta: dc.likes },
        { label: "comments", value: post.comments ?? 0, delta: dc.comments },
        { label: "saves", value: post.saves ?? 0, delta: dc.saves },
      ];
    }
    // TikTok, YouTube, Facebook, Twitter, etc.
    return [
      { label: "views", value: post.views ?? 0, delta: dc.views },
      { label: "likes", value: post.likes ?? 0, delta: dc.likes },
      { label: "comments", value: post.comments ?? 0, delta: dc.comments },
      { label: "shares", value: post.shares ?? 0, delta: dc.shares },
    ];
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 tracking-widest">{label}</p>
        <div className="flex gap-1 bg-stone-100 dark:bg-zinc-800/50 rounded-lg p-0.5">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => setSort(opt.key)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${
                sort === opt.key
                  ? "bg-white dark:bg-zinc-900 text-stone-800 dark:text-zinc-200 shadow-sm"
                  : "text-stone-500 dark:text-zinc-500 hover:text-stone-700 dark:hover:text-zinc-300"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 divide-y divide-stone-100 dark:divide-zinc-800/60">
        {sorted.map((post: any, idx: number) => {
          const dc = post.daily_change || {};
          const mainVal = post[primaryMetric] || post.likes || 0;
          const mainDelta = dc[primaryMetric] || dc.likes || 0;
          const growthRate = mainVal > 0 ? (mainDelta / mainVal) * 100 : 0;
          const isTrending = growthRate > 5;
          const stats = getStats(post);

          return (
            <div key={idx} className="flex items-center gap-4 px-5 py-3">
              <span className="text-sm font-bold text-stone-300 dark:text-zinc-700 w-5 text-center flex-shrink-0">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-stone-800 dark:text-zinc-200 truncate">{post.label || "Untitled post"}</p>
                  {isTrending && <span className="text-[9px] bg-orange-100 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0">trending</span>}
                </div>
                {post.date && <p className="text-[10px] text-stone-400 dark:text-zinc-600">{post.date}</p>}
              </div>
              <div className="flex gap-4 flex-shrink-0 text-xs text-stone-500 dark:text-zinc-500">
                {stats.map(s => (
                  <VideoStat key={s.label} label={s.label} value={s.value} delta={s.delta} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Overview Chart (combined metrics) ─── */

function OverviewChart({ metrics }: { metrics: Record<string, InsightMetric> }) {
  const colors = useChartColors();
  const metricsWithValues = Object.entries(metrics).filter(([_, m]) => m.values && m.values.length > 1);
  if (metricsWithValues.length === 0) return null;

  const metricTotals = metricsWithValues.map(([name, m]) => ({
    name,
    title: m.title || formatMetricName(name),
    total: m.values.reduce((s, v) => s + v.value, 0)
  })).sort((a, b) => b.total - a.total).slice(0, 4);

  // Compute max per metric to detect scale differences
  const metricMaxes: Record<string, number> = {};
  for (const [name, metric] of metricsWithValues) {
    metricMaxes[name] = Math.max(...metric.values.map(v => v.value), 1);
  }

  // Find the median max — we'll scale outliers down to this range
  const sortedMaxes = metricTotals.map(m => metricMaxes[m.name]).sort((a, b) => a - b);
  const medianMax = sortedMaxes[Math.floor(sortedMaxes.length / 2)];

  // Compute a scale factor per metric: metrics within 5x of median stay as-is,
  // outliers get scaled down to fit. This preserves real Y values for most metrics
  // while bringing huge outliers into the visible range.
  const scaleFactors: Record<string, number> = {};
  const scaledNames: string[] = [];
  for (const m of metricTotals) {
    const max = metricMaxes[m.name];
    if (max > medianMax * 5) {
      scaleFactors[m.name] = medianMax / max;
      scaledNames.push(m.name);
    } else {
      scaleFactors[m.name] = 1;
    }
  }

  // Build chart data — apply scale factors to outlier metrics
  const rawDateMap: Record<string, Record<string, number>> = {};
  const chartDateMap: Record<string, Record<string, number>> = {};

  for (const [name, metric] of metricsWithValues) {
    const factor = scaleFactors[name] ?? 1;
    for (const v of metric.values) {
      const date = formatDate(v.end_time);
      if (!rawDateMap[date]) rawDateMap[date] = {};
      if (!chartDateMap[date]) chartDateMap[date] = {};
      rawDateMap[date][name] = v.value;
      chartDateMap[date][name] = v.value * factor;
    }
  }

  const chartData = Object.entries(chartDateMap).map(([date, values]) => ({ date, ...values }));
  if (chartData.length < 2) return null;

  // Custom tooltip shows real (unscaled) values
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        backgroundColor: colors.tooltipBg,
        border: `1px solid ${colors.tooltipBorder}`,
        borderRadius: "12px",
        fontSize: "12px",
        padding: "8px 12px",
      }}>
        <p style={{ fontWeight: 600, marginBottom: 4, color: colors.tooltipText }}>{label}</p>
        {payload.map((entry: any) => {
          const rawValue = rawDateMap[label]?.[entry.dataKey];
          return (
            <p key={entry.dataKey} style={{ color: entry.color, margin: "2px 0" }}>
              {entry.name}: <strong>{rawValue != null ? rawValue.toLocaleString() : entry.value}</strong>
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-stone-800 dark:text-zinc-200">Overview</h4>
        <div className="flex flex-wrap gap-3">
          {metricTotals.map((m, i) => (
            <div key={m.name} className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-zinc-500">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i] }} />
              <span>{m.title}{scaledNames.includes(m.name) ? " (scaled)" : ""}</span>
            </div>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
          <defs>
            {metricTotals.map((m, i) => (
              <linearGradient key={m.name} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_COLORS[i]} stopOpacity={0.3} />
                <stop offset="100%" stopColor={CHART_COLORS[i]} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: colors.axis }} stroke={colors.grid} />
          <YAxis tick={{ fontSize: 11, fill: colors.axis }} stroke={colors.grid} width={50} tickFormatter={(v: number) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : String(Math.round(v))} />
          <Tooltip content={<CustomTooltip />} />
          {metricTotals.map((m, i) => (
            <Area
              key={m.name}
              type="monotone"
              dataKey={m.name}
              name={m.title}
              stroke={CHART_COLORS[i]}
              fill={`url(#grad-${i})`}
              strokeWidth={2}
              dot={{ r: 3, strokeWidth: 0, fill: CHART_COLORS[i] }}
              activeDot={{ r: 5, strokeWidth: 0 }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── Individual Metric Chart ─── */

function MetricChart({ name, metric, color }: { name: string; metric: InsightMetric; color: string }) {
  const colors = useChartColors();
  const values = metric.values || [];
  const totalValue = (metric as any).total_value?.value;
  const title = metric.title || formatMetricName(name);
  // Earnings metrics get currency formatting in tooltips and the summary card
  const isMoney = /earnings/i.test(name);
  const fmt = (v: number) =>
    isMoney
      ? new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v)
      : v.toLocaleString();

  // No values and no total_value — skip
  if (values.length === 0 && totalValue == null) return null;

  // If we only have total_value but no daily breakdown, show a summary card
  if (values.length === 0 && totalValue != null) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-stone-800 dark:text-zinc-200 truncate">{title}</h4>
            {metric.description && (
              <p className="text-[11px] text-stone-400 dark:text-zinc-600 mt-0.5 line-clamp-2">{metric.description}</p>
            )}
          </div>
          <div className="text-right ml-3 flex-shrink-0">
            <p className="text-2xl font-bold" style={{ color }}>{fmt(totalValue)}</p>
            <p className="text-[10px] uppercase text-stone-400 dark:text-zinc-600 tracking-wide">Total</p>
          </div>
        </div>
      </div>
    );
  }

  // Detect cumulative/lifetime metrics — values represent running totals, not daily increments
  const isCumulative = metric.period === 'lifetime'
    || /\b(total follows|total.?fans|follower.?count)\b/i.test(title)
    || /\b(page_follows|follower_count)\b/i.test(name);

  const sum = values.reduce((s, v) => s + v.value, 0);
  const latest = values.length > 0 ? values[values.length - 1].value : 0;
  const max = Math.max(...values.map(v => v.value));
  const min = Math.min(...values.map(v => v.value));
  const hasNegative = min < 0;

  // For cumulative metrics: show latest value and change over period
  // For incremental metrics: show sum and average
  const displayTotal = isCumulative ? latest : (totalValue != null ? totalValue : sum);
  const displayTotalLabel = isCumulative ? "Current" : "Total";

  let displaySecondary: number;
  let displaySecondaryLabel: string;
  if (isCumulative && values.length >= 2) {
    displaySecondary = values[values.length - 1].value - values[0].value;
    displaySecondaryLabel = "Change";
  } else {
    displaySecondary = values.length > 0 ? Math.round(sum / values.length) : 0;
    displaySecondaryLabel = "Avg";
  }

  const chartData = values.map(v => ({
    date: formatDate(v.end_time),
    value: v.value,
  }));

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-stone-800 dark:text-zinc-200 truncate">{title}</h4>
          {metric.description && (
            <p className="text-[11px] text-stone-400 dark:text-zinc-600 mt-0.5 line-clamp-1">{metric.description}</p>
          )}
        </div>
        <div className="flex gap-3 text-xs text-stone-500 dark:text-zinc-500 ml-3 flex-shrink-0">
          <div className="text-center">
            <p className="font-bold text-stone-800 dark:text-zinc-200 text-base">{displayTotal.toLocaleString()}</p>
            <p className="text-[10px] uppercase tracking-wide">{displayTotalLabel}</p>
          </div>
          <div className="text-center">
            <p className={`font-bold text-base ${isCumulative && displaySecondary > 0 ? "text-green-600 dark:text-green-400" : isCumulative && displaySecondary < 0 ? "text-red-600 dark:text-red-400" : "text-stone-800 dark:text-zinc-200"}`}>
              {isCumulative && displaySecondary > 0 ? "+" : ""}{displaySecondary.toLocaleString()}
            </p>
            <p className="text-[10px] uppercase tracking-wide">{displaySecondaryLabel}</p>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.axis }} stroke={colors.grid} />
          <YAxis hide domain={[hasNegative ? min * 1.2 : 0, max * 1.1 || 1]} />
          <Tooltip
            contentStyle={{
              backgroundColor: colors.tooltipBg,
              border: `1px solid ${colors.tooltipBorder}`,
              borderRadius: "10px",
              fontSize: "12px",
              padding: "6px 10px",
              color: colors.tooltipText,
            }}
            formatter={(value: any) => [fmt(Number(value || 0)), title]}
            labelStyle={{ fontWeight: 600, color: colors.tooltipText }}
            cursor={{ fill: colors.grid }}
          />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} maxBarSize={40}>
            {hasNegative && chartData.map((entry, idx) => (
              <Cell key={idx} fill={entry.value < 0 ? "#ef4444" : color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── Totals Comparison Chart (horizontal bars for total_value-only metrics) ─── */

function TotalsChart({ metrics }: { metrics: [string, InsightMetric][] }) {
  const colors = useChartColors();
  const sorted = metrics
    .map(([name, m], i) => ({
      name: m.title || formatMetricName(name),
      value: (m as any).total_value?.value ?? 0,
      color: CHART_COLORS[i % CHART_COLORS.length],
      description: m.description || "",
    }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  if (sorted.length === 0) return null;

  // Build chart data as a single row with each metric as its own key
  const chartRow: Record<string, number> = {};
  for (const d of sorted) chartRow[d.name] = d.value;

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 p-5">
      <ResponsiveContainer width="100%" height={Math.max(200, sorted.length * 48 + 20)}>
        <BarChart data={sorted} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: colors.axis }} stroke={colors.grid} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: colors.axis }} width={140} stroke="transparent" />
          <Tooltip
            contentStyle={{
              backgroundColor: colors.tooltipBg,
              border: `1px solid ${colors.tooltipBorder}`,
              borderRadius: "12px",
              fontSize: "12px",
              padding: "8px 12px",
              color: colors.tooltipText,
            }}
            formatter={(value: any) => [Number(value || 0).toLocaleString(), ""]}
            labelStyle={{ fontWeight: 600, color: colors.tooltipText }}
            itemStyle={{ color: colors.tooltipText }}
            cursor={{ fill: colors.grid, opacity: 0.5 }}
          />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={32} fill={CHART_COLORS[0]}>
            {sorted.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {/* Metric descriptions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-stone-100 dark:border-zinc-800">
        {sorted.map((d, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: d.color }} />
            <div>
              <p className="text-xs font-medium text-stone-700 dark:text-zinc-300">{d.name}: <span className="font-bold">{d.value.toLocaleString()}</span></p>
              {d.description && <p className="text-[10px] text-stone-400 dark:text-zinc-600 line-clamp-1">{d.description}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Post Growth Chart ─── */

function PostGrowthChart({ history }: { history: Record<string, Record<string, { value: number; timestamp: string }[]>> }) {
  const colors = useChartColors();

  // Merge all platforms' metrics into a unified time series
  // Focus on views and likes as the primary growth indicators
  const PRIMARY_METRICS = ["views", "likes", "comments", "shares"];
  const METRIC_COLORS: Record<string, string> = { views: "#f97316", likes: "#3b82f6", comments: "#10b981", shares: "#8b5cf6" };

  const dateMap: Record<string, Record<string, number>> = {};
  const metricsFound: string[] = [];

  for (const [_platform, metrics] of Object.entries(history)) {
    for (const metricName of PRIMARY_METRICS) {
      const values = metrics[metricName];
      if (!values || values.length < 2) continue;
      if (!metricsFound.includes(metricName)) metricsFound.push(metricName);
      for (const v of values) {
        const date = formatDate(v.timestamp);
        if (!dateMap[date]) dateMap[date] = {};
        // Sum across platforms if same metric exists on multiple
        dateMap[date][metricName] = (dateMap[date][metricName] || 0) + v.value;
      }
    }
  }

  const chartData = Object.entries(dateMap).map(([date, values]) => ({ date, ...values }));
  if (chartData.length < 2 || metricsFound.length === 0) return null;

  return (
    <div className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-4">
      <div className="flex items-center gap-3 mb-3">
        {metricsFound.map(m => (
          <div key={m} className="flex items-center gap-1 text-[10px] text-stone-500 dark:text-zinc-500">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: METRIC_COLORS[m] || "#999" }} />
            <span className="capitalize">{m}</span>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
          <defs>
            {metricsFound.map(m => (
              <linearGradient key={m} id={`post-grad-${m}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={METRIC_COLORS[m] || "#999"} stopOpacity={0.3} />
                <stop offset="100%" stopColor={METRIC_COLORS[m] || "#999"} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: colors.axis }} stroke={colors.grid} />
          <YAxis tick={{ fontSize: 10, fill: colors.axis }} stroke={colors.grid} width={45} />
          <Tooltip
            contentStyle={{
              backgroundColor: colors.tooltipBg,
              border: `1px solid ${colors.tooltipBorder}`,
              borderRadius: "10px",
              fontSize: "11px",
              padding: "6px 10px",
              color: colors.tooltipText,
            }}
            labelStyle={{ fontWeight: 600, color: colors.tooltipText }}
            itemStyle={{ color: colors.tooltipText }}
          />
          {metricsFound.map(m => (
            <Area
              key={m}
              type="monotone"
              dataKey={m}
              name={m.charAt(0).toUpperCase() + m.slice(1)}
              stroke={METRIC_COLORS[m] || "#999"}
              fill={`url(#post-grad-${m})`}
              strokeWidth={2}
              dot={{ r: 3, strokeWidth: 0, fill: METRIC_COLORS[m] || "#999" }}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── Post Analytics View ─── */

function PostAnalyticsView({
  posts,
  postsLoading,
  selectedPost,
  postAnalytics,
  postAnalyticsLoading,
  postAnalyticsError,
  onSelectPost,
  onClose,
}: {
  posts: SocialPost[];
  postsLoading: boolean;
  selectedPost: SocialPost | null;
  postAnalytics: PostAnalytics | null;
  postAnalyticsLoading: boolean;
  postAnalyticsError: string;
  onSelectPost: (post: SocialPost) => void;
  onClose: () => void;
}) {
  if (postsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-stone-100 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-stone-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </div>
        <p className="text-stone-500 dark:text-zinc-500">No published posts to analyze</p>
      </div>
    );
  }

  return (
    <div>
      {/* Post analytics detail modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-stone-200/60 dark:border-zinc-800/60 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-50 dark:bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-stone-900 dark:text-white">Post Analytics</h3>
              </div>
              <button onClick={onClose} className="text-stone-400 dark:text-zinc-600 hover:text-stone-600 dark:hover:text-zinc-400 p-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3 mb-4">
              <p className="text-sm text-stone-700 dark:text-zinc-300 line-clamp-2">{selectedPost.text || "No text"}</p>
              {selectedPost.published_at && (
                <p className="text-[10px] text-stone-400 dark:text-zinc-600 mt-1">{new Date(selectedPost.published_at).toLocaleString()}</p>
              )}
            </div>

            {postAnalyticsLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {postAnalyticsError && (
              <div className="p-3 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-xl text-sm">{postAnalyticsError}</div>
            )}

            {postAnalytics && !postAnalyticsLoading && (
              <div className="space-y-4">
                {Object.keys(postAnalytics.totals).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-2 tracking-widest">Totals</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(postAnalytics.totals).map(([key, value]) => (
                        <div key={key} className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold text-stone-900 dark:text-white">{typeof value === "number" ? value.toLocaleString() : value}</p>
                          <p className="text-[10px] uppercase text-stone-400 dark:text-zinc-600 font-medium tracking-wide">{key.replace(/_/g, " ")}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(postAnalytics.totals).length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-stone-500 dark:text-zinc-500 text-sm">No metrics available yet</p>
                    <p className="text-stone-400 dark:text-zinc-600 text-xs mt-1">Analytics may take a few minutes after posting</p>
                  </div>
                )}

                {/* Per-post growth chart from stored history */}
                {postAnalytics.history && Object.keys(postAnalytics.history).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-2 tracking-widest">Growth Over Time</p>
                    <PostGrowthChart history={postAnalytics.history} />
                  </div>
                )}

                {Object.keys(postAnalytics.analytics).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-2 tracking-widest">By Platform</p>
                    <div className="space-y-2">
                      {Object.entries(postAnalytics.analytics).map(([platform, entry]) => {
                        const info = getPlatformInfo(platform);
                        const hasMetrics = entry.metrics && Object.keys(entry.metrics).length > 0;
                        return (
                          <div key={platform} className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-5 h-5 ${info.color} text-white rounded flex items-center justify-center flex-shrink-0`}>
                                <PlatformIcon platform={platform} className="w-3 h-3" />
                              </span>
                              <span className="text-sm font-medium text-stone-800 dark:text-zinc-200">
                                {entry.account_name || info.name}
                              </span>
                              {entry.platform_post_url && (
                                <a href={entry.platform_post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium ml-auto">
                                  view
                                </a>
                              )}
                            </div>
                            {hasMetrics ? (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {Object.entries(entry.metrics!).filter(([_, v]) => v != null).map(([key, value]) => (
                                  <span key={key} className="text-xs text-stone-600 dark:text-zinc-400">
                                    <span className="font-semibold text-stone-800 dark:text-zinc-200">{typeof value === "number" ? value.toLocaleString() : value}</span>
                                    {" "}{key.replace(/_/g, " ")}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-stone-400 dark:text-zinc-600">{entry.reason || "No metrics available"}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Posts list */}
      <div className="space-y-3">
        {posts.map(post => {
          const results = post.platform_results ? Object.values(post.platform_results) : [];
          const platforms = results.filter((r: any) => r.status === "success" || r.status === "published");

          return (
            <button
              key={post.id}
              onClick={() => onSelectPost(post)}
              className="w-full text-left bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 hover:border-orange-300 dark:hover:border-orange-500/30 transition-all p-4 group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-stone-100 dark:bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-orange-50 dark:group-hover:bg-orange-500/10 transition-colors">
                  <svg className="w-5 h-5 text-stone-400 dark:text-zinc-600 group-hover:text-orange-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-800 dark:text-zinc-200 line-clamp-2 leading-relaxed">{post.text || "No text"}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex -space-x-1">
                      {platforms.slice(0, 5).map((r: any, i: number) => {
                        const info = getPlatformInfo(r.platform);
                        return (
                          <span key={i} className={`w-5 h-5 ${info.color} text-white rounded-full flex items-center justify-center border-2 border-white dark:border-zinc-900`}>
                            <PlatformIcon platform={r.platform} className="w-2.5 h-2.5" />
                          </span>
                        );
                      })}
                    </div>
                    {post.published_at && (
                      <span className="text-xs text-stone-400 dark:text-zinc-600">
                        {new Date(post.published_at).toLocaleDateString()}
                      </span>
                    )}
                    <span className="text-xs text-stone-400 dark:text-zinc-600 ml-auto">
                      {post.successful_count}/{post.total_accounts} platforms
                    </span>
                  </div>
                </div>
                <svg className="w-4 h-4 text-stone-300 dark:text-zinc-700 group-hover:text-orange-500 transition-colors flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
